import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Close,
  ArrowForward,
  ArrowBack,
  SecurityOutlined,
  GroupOutlined,
  PaymentOutlined,
  CheckCircleOutlined,
} from "@mui/icons-material";

interface GroupAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
  groupName?: string;
  isVerified?: boolean;
  hasNoBankAccount?: boolean;
}

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  background: `linear-gradient(145deg, ${theme.palette.background.paper} 0%, #f7faf7 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "16px",
  position: "relative",
  padding: "32px",
  maxWidth: "500px",
  margin: "0 auto",
  minHeight: "600px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: "white",
  fontWeight: 600,
  padding: "12px 24px",
  borderRadius: "8px",
  textTransform: "none",
  fontSize: "16px",
  boxShadow: "0 4px 15px rgba(46, 125, 50, 0.3)",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, #1B5E20 100%)`,
    transform: "translateY(-1px)",
    boxShadow: "0 6px 20px rgba(46, 125, 50, 0.4)",
  },
}));

export default function GroupAgreementModal({
  isOpen,
  onClose,
  onAgree,
  groupName = "Sanduuq",
  isVerified = false,
  hasNoBankAccount = false,
}: GroupAgreementModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const steps = ["Welcome", "Responsibilities", "Agreement"];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAgree = () => {
    if (dontShowAgain) {
      localStorage.setItem("sandooq_agreement_accepted", "true");
    }
    onAgree();
    setActiveStep(0);
    setAgreed(false);
    setDontShowAgain(false);
  };

  const handleClose = () => {
    onClose();
    setActiveStep(0);
    setAgreed(false);
    setDontShowAgain(false);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "primary.main", mb: 2 }}
            >
              Welcome to {groupName}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.primary", mb: 3, lineHeight: 1.6 }}
            >
              You're about to join an amazing circle! Before we get started,
              let's go over some important details to ensure everyone has a
              great experience.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              This will only take a minute, and it's important for everyone's
              security.
            </Typography>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "primary.main",
                mb: 3,
                textAlign: "center",
              }}
            >
              Your Responsibilities
            </Typography>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Security & Trust are our top priorities!
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                <PaymentOutlined
                  sx={{ color: "primary.main", mr: 2, mt: 0.5 }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Payment Commitment
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    You must pay your contribution{" "}
                    <strong>before the due date</strong> every cycle.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                <SecurityOutlined
                  sx={{ color: "error.dark", mr: 2, mt: 0.5 }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Immediate Consequences
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Missing a payment will result in{" "}
                    <strong>immediate expulsion</strong> from the app.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <GroupOutlined sx={{ color: "primary.main", mr: 2, mt: 0.5 }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Community Trust
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Your reliability affects everyone in the circle. We maintain
                    high standards for all members.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                textAlign: "center",
                fontStyle: "italic",
              }}
            >
              These rules ensure fairness and security for all members.
            </Typography>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: "primary.main", mb: 3 }}
            >
              Ready to Join?
            </Typography>

            <Typography
              variant="body1"
              sx={{ color: "text.primary", mb: 3, lineHeight: 1.6 }}
            >
              By checking the box below, you confirm that you understand and
              agree to all the responsibilities and consequences outlined above.
            </Typography>

            {(!isVerified || hasNoBankAccount) && (
              <Alert severity="warning" sx={{ borderRadius: 2, mb: 1, textAlign: "left" }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Before the circle starts, please complete:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {!isVerified && (
                    <li>
                      <Typography variant="body2">
                        <a href="/onboarding?step=3" style={{ fontWeight: 600, color: "inherit" }}>
                          Identity verification
                        </a>
                      </Typography>
                    </li>
                  )}
                  {hasNoBankAccount && (
                    <li>
                      <Typography variant="body2">
                        <a href="/profile?tab=finances" style={{ fontWeight: 600, color: "inherit" }}>
                          Bank account for payouts
                        </a>
                      </Typography>
                    </li>
                  )}
                </Box>
                <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
                  If not completed within 14 days of the circle starting, you will be removed and any contributions made will be forfeited.
                </Typography>
              </Alert>
            )}

            <Box
              sx={{
                mb: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", textAlign: "left" }}
                  >
                    I understand and agree to the payment responsibilities and
                    consequences
                  </Typography>
                }
              />
              {/* <FormControlLabel
                control={
                  <Checkbox
                    checked={dontShowAgain}
                    onChange={(e) => setDontShowAgain(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Don't show this agreement again for future groups
                  </Typography>
                }
              /> */}
            </Box>

            {agreed && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You're all set to join the circle.
                </Typography>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {/* Main Content */}
        <StyledDialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          {/* Close button */}
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.08)",
              },
            }}
          >
            <Close sx={{ fontSize: 20, color: "text.secondary" }} />
          </IconButton>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          <Box sx={{ mb: 2 }}>{renderStepContent(activeStep)}</Box>

          {/* Navigation Buttons */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<ArrowBack />}
              sx={{ color: "text.secondary" }}
            >
              Back
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              {activeStep < steps.length - 1 ? (
                <GradientButton onClick={handleNext} endIcon={<ArrowForward />}>
                  Next
                </GradientButton>
              ) : (
                <GradientButton
                  onClick={handleAgree}
                  disabled={!agreed}
                  endIcon={<CheckCircleOutlined />}
                  sx={{
                    opacity: agreed ? 1 : 0.5,
                  }}
                >
                  Join Circle
                </GradientButton>
              )}
            </Box>
          </Box>
        </StyledDialogContent>
      </Box>
    </Dialog>
  );
}
